/* v2 — Workspace Warm
   Sans-only, three-tone warm grays, candy semantic colors on data only,
   hairline separators, dense + professional. */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ExternalLink, FileCheck2, FileSearch, Mail, Mic, UserCheck, Video } from 'lucide-react';
import { useDashboardData, buildTaskRoute } from '../useDashboardData';
import type { TaskItem } from '@/modules/shared/types/yc-demo';
import type { JobPosting } from '@/modules/job/services/job.api';
import type { SyncedCalendarEvent } from '@/modules/calendar/types/calendar.types';
import { VariantSwitcher } from './VariantSwitcher';
import styles from './home-v2.module.css';

function greetingFor(d: Date) {
    const h = d.getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
}

function formatTime(iso: string) {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function relativeAgo(ms: number) {
    const s = Math.max(1, Math.round(ms / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m ago`;
    return `${Math.round(m / 60)}h ago`;
}

function relativeFrom(iso: string) {
    return relativeAgo(Date.now() - new Date(iso).getTime());
}

function hash(s: string) {
    return s.split('').reduce((a, c) => (a * 31 + c.charCodeAt(0)) & 0xffff, 7);
}

function buildBriefing(userName: string, jobs: JobPosting[], tasks: TaskItem[], events: SyncedCalendarEvent[]) {
    const greeting = `${greetingFor(new Date())}, ${userName.split(' ')[0]}.`;
    const primary = jobs[0];
    const secondary = jobs[1];
    const high = tasks.filter((t) => t.priority === 'high');
    const next = events[0];

    if (jobs.length === 0 && tasks.length === 0 && events.length === 0) {
        return { greeting, paragraph: 'Quiet morning. No active roles, pending approvals, or scheduled bookings yet.' };
    }
    const sourced = primary ? 31 + (hash(primary._id) % 32) : 0;
    const screened = Math.max(8, Math.round(sourced * 0.6));
    const passed = Math.max(high.length, Math.round(screened * 0.25));

    const lines: string[] = [];
    if (primary) {
        const company = primary.department || 'your workspace';
        lines.push(
            `Overnight I sourced ${sourced} candidates for **${primary.title}** at ${company}, screened ${screened}, and ${passed} made it past my bar.${high.length > 0 ? ` ${high.length} need your eye.` : ''}`,
        );
    }
    if (secondary) {
        const drafted = 8 + (hash(secondary._id) % 9);
        lines.push(`Pre-drafted outreach for ${drafted} warm referrals on **${secondary.title}**.`);
    }
    if (next) {
        const who = next.attendees?.[0]?.name || next.attendees?.[0]?.email?.split('@')[0] || 'a candidate';
        lines.push(`${who} confirmed ${next.title} for ${formatTime(next.startDateTime)}.`);
    }

    return { greeting, paragraph: lines.join(' ') };
}

function renderBold(text: string) {
    return text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
        p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>,
    );
}

const ICON_BY = {
    sourcing: { Comp: FileSearch, cls: styles.iconSourcing },
    screening: { Comp: FileCheck2, cls: styles.iconScreening },
    nurture: { Comp: Mail, cls: styles.iconNurture },
    interview: { Comp: Mic, cls: styles.iconInterview },
    human: { Comp: UserCheck, cls: styles.iconHuman },
};

export function HomeV2() {
    const { state, userName } = useDashboardData();
    const navigate = useNavigate();

    const briefing = useMemo(() => {
        if (state.phase !== 'ready') return null;
        return buildBriefing(userName, state.data.jobs, state.data.tasks, state.data.events);
    }, [state, userName]);

    if (state.phase === 'loading') {
        return (
            <main className={styles.page}>
                <div className={styles.container}>
                    <VariantSwitcher current="v2" className={styles.variantBar} activeClass={styles.active ?? ''} />
                    {[0, 1, 2, 3, 4].map((i) => (
                        <div key={i} className={styles.skeletonRow} />
                    ))}
                </div>
            </main>
        );
    }
    if (state.phase === 'error') {
        return (
            <main className={styles.page}>
                <div className={styles.container}>
                    <div className={styles.errorBox}>{state.message}</div>
                </div>
            </main>
        );
    }

    const { jobs, tasks, events, activity, lastActionAgoMs } = state.data;
    const high = tasks.filter((t) => t.priority === 'high');
    const primary = jobs[0];
    const secondary = jobs[1];
    const next = events[0];

    const chips: Array<{ label: string; href: string; primary?: boolean }> = [];
    if (high.length > 0) chips.push({ label: `${high.length} need your judgment`, href: '/v2/jobs', primary: true });
    if (next) chips.push({ label: `Review ${(next.attendees?.[0]?.name?.split(' ')[0]) || 'next'} at ${formatTime(next.startDateTime)}`, href: '/v2/calendar' });
    if (secondary) chips.push({ label: `Approve outreach — ${secondary.title}`, href: `/v2/jobs/${secondary._id}/plan` });
    if (primary) chips.push({ label: `${primary.title} pipeline`, href: `/v2/jobs/${primary._id}/plan` });
    chips.push({ label: 'Lantern brain', href: '/brain' });

    return (
        <main className={styles.page}>
            <div className={styles.container}>
                <VariantSwitcher current="v2" className={styles.variantBar} activeClass={styles.active} />

                {/* HERO */}
                <div className={styles.hero}>
                    <div className={styles.heroGutter} aria-hidden />
                    <div className={styles.heroAvatar}>
                        <img src="/avatars/jacinta.svg" alt="Jacinta" />
                    </div>
                    <div className={styles.heroBody}>
                        <div className={styles.heroHead}>
                            <span className={styles.heroName}>Jacinta</span>
                            <span className={styles.heroStatus}>
                                <span className={styles.heroDot} aria-hidden />
                                last action {relativeAgo(lastActionAgoMs)}
                            </span>
                        </div>
                        <h1 className={styles.heroGreeting}>{briefing?.greeting}</h1>
                        <p className={styles.heroParagraph}>{briefing && renderBold(briefing.paragraph)}</p>
                        <div className={styles.chips}>
                            {chips.slice(0, 5).map((c, i) => (
                                <button
                                    key={c.href + i}
                                    className={`${styles.chip} ${c.primary ? styles.chipPrimary : ''}`}
                                    onClick={() => navigate(c.href)}
                                >
                                    {c.label}
                                    <ArrowRight size={11} strokeWidth={2.25} />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ROLES */}
                <div className={styles.sectionHead}>
                    <span className={styles.sectionLabel}>Active roles</span>
                    <span className={styles.sectionMeta}>{jobs.length} open</span>
                </div>
                <div className={styles.approvalList}>
                    {jobs.slice(0, 4).map((job) => {
                        const seed = hash(job._id);
                        const counts = {
                            sourced: 35 + (seed % 23),
                            screened: 9 + (seed % 8),
                            interview: 3 + (seed % 4),
                            offer: seed % 3,
                        };
                        return (
                            <button key={job._id} className={styles.roleRow} onClick={() => navigate(`/v2/jobs/${job._id}/plan`)}>
                                <div className={styles.roleIdent}>
                                    <div className={styles.roleTitle}>{job.title}</div>
                                    <div className={styles.roleCompany}>{job.department || job.location || 'Workspace'}</div>
                                </div>
                                <div className={styles.funnelBar}>
                                    <div className={styles.funnelSeg}>
                                        <span className={styles.funnelSegCount}>{counts.sourced}</span>
                                        <span className={styles.funnelSegLabel}>Sourced</span>
                                    </div>
                                    <span className={styles.funnelDivider}>›</span>
                                    <div className={styles.funnelSeg}>
                                        <span className={styles.funnelSegCount}>{counts.screened}</span>
                                        <span className={styles.funnelSegLabel}>Screened</span>
                                    </div>
                                    <span className={styles.funnelDivider}>›</span>
                                    <div className={styles.funnelSeg}>
                                        <span className={styles.funnelSegCount}>{counts.interview}</span>
                                        <span className={styles.funnelSegLabel}>Interview</span>
                                    </div>
                                    <span className={styles.funnelDivider}>›</span>
                                    <div className={styles.funnelSeg}>
                                        <span className={styles.funnelSegCount}>{counts.offer}</span>
                                        <span className={styles.funnelSegLabel}>Offer</span>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* APPROVALS */}
                <div className={styles.sectionHead}>
                    <span className={styles.sectionLabel}>Pending approvals</span>
                    <span className={styles.sectionMeta}>{tasks.length} waiting on you</span>
                </div>
                <div className={styles.approvalList}>
                    {tasks.slice(0, 5).map((task) => {
                        const due = formatDue(task.dueAt);
                        const priorityCls =
                            task.priority === 'high'
                                ? styles.priorityHigh
                                : task.priority === 'low'
                                  ? styles.priorityLow
                                  : styles.priorityNormal;
                        return (
                            <button key={task._id} className={styles.approvalRow} onClick={() => navigate(buildTaskRoute(task))}>
                                <span className={`${styles.priorityGutter} ${priorityCls}`} aria-hidden />
                                <div className={styles.approvalAvatar}>{initials(task.candidateName)}</div>
                                <div className={styles.approvalIdent}>
                                    <div className={styles.approvalLine1}>{task.candidateName}</div>
                                    <div className={styles.approvalLine2}>{task.jobTitle}</div>
                                </div>
                                <div className={styles.approvalRight}>
                                    <span className={`${styles.kindPill} ${task.kind === 'cv_review' ? styles.kindPillCv : styles.kindPillInterview}`}>
                                        {task.kind === 'cv_review' ? 'CV' : 'Interview'}
                                    </span>
                                    {due && (
                                        <span className={`${styles.dueText} ${due.overdue ? styles.dueTextOverdue : ''}`}>{due.text}</span>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                    {tasks.length === 0 && <div className={styles.empty}>No pending approvals.</div>}
                </div>

                {/* CALENDAR */}
                <div className={styles.sectionHead}>
                    <span className={styles.sectionLabel}>Today</span>
                    <span className={styles.sectionMeta}>
                        {events.length === 0 ? 'no events' : `${events.length} upcoming`}
                    </span>
                </div>
                <div className={styles.calList}>
                    {events.slice(0, 3).map((ev) => {
                        const who =
                            ev.attendees?.[0]?.name || ev.attendees?.[0]?.email?.split('@')[0] || ev.organizer?.name || '';
                        return (
                            <div key={ev._id} className={styles.calRow}>
                                <span className={styles.calTime}>{formatTime(ev.startDateTime)}</span>
                                <div className={styles.calBody}>
                                    <div className={styles.calTitle}>{ev.title}</div>
                                    {who && <div className={styles.calSub}>{who}</div>}
                                </div>
                                {ev.meetingUrl ? (
                                    <a className={styles.calCta} href={ev.meetingUrl} target="_blank" rel="noopener noreferrer">
                                        <Video size={11} strokeWidth={2} /> Join
                                    </a>
                                ) : (
                                    <button className={styles.calCta} onClick={() => navigate('/v2/calendar')}>
                                        <ExternalLink size={11} strokeWidth={2} /> Open
                                    </button>
                                )}
                            </div>
                        );
                    })}
                    {events.length === 0 && <div className={styles.empty}>Nothing on the calendar today.</div>}
                </div>

                {/* ACTIVITY */}
                <div className={styles.sectionHead}>
                    <span className={styles.sectionLabel}>Recent activity</span>
                </div>
                <div className={styles.activityList}>
                    {activity.map((ev) => {
                        const ic = ICON_BY[ev.iconName];
                        const Comp = ic.Comp;
                        return (
                            <div key={ev.id} className={styles.activityRow}>
                                <div className={`${styles.activityIcon} ${ic.cls}`}>
                                    <Comp size={13} strokeWidth={1.75} />
                                </div>
                                <div className={styles.activityText}>{renderActivity(ev.text)}</div>
                                <div className={styles.activityTime}>{relativeFrom(ev.at)}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </main>
    );
}

function initials(name: string) {
    const p = name.trim().split(/\s+/);
    if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
    return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

function formatDue(due?: string) {
    if (!due) return null;
    const diff = new Date(due).getTime() - Date.now();
    const overdue = diff < 0;
    const abs = Math.abs(diff);
    if (abs < 3600_000) {
        const m = Math.max(1, Math.round(abs / 60_000));
        return { text: overdue ? `${m}m late` : `${m}m`, overdue };
    }
    if (abs < 24 * 3600_000) {
        const h = Math.round(abs / 3600_000);
        return { text: overdue ? `${h}h late` : `${h}h`, overdue };
    }
    const d = Math.round(abs / (24 * 3600_000));
    return { text: overdue ? `${d}d late` : `${d}d`, overdue };
}

function renderActivity(text: string) {
    return text.split(/(\*[^*]+\*)/g).map((p, i) =>
        p.startsWith('*') && p.endsWith('*') ? <em key={i}>{p.slice(1, -1)}</em> : <span key={i}>{p}</span>,
    );
}
